import React, { Fragment, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Button, Card, Form, Image, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";

import { PageSpinner } from "@cdk-uip/react-page-spinner";

import api from "../../api/sezForm.resource";
import Dialog from "../common/Dialog";
import ErrorDialog from "../common/ErrorDialog";
import {
  defaultToastOptions,
  positiveNumberPattern,
  startingSpacePattern
} from "../../constants/constants";
import SubmitSuccess from "./SubmitSuccess";
import { imageCompressor } from "../../util/helpers";
import useValidation from "../../util/useValidation";

import "../../styles/sezForm.css";

const SEZForm = () => {
  const [employeeIdDetails, setEmployeeIdDetails] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [submitScreen, setSubmitScreen] = useState(false);
  const [submitAlert, setSubmitAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({
    employeePhoto: false,
    employeePrevSezCard: false
  });
  const identityCardFormRef = useRef(null);
  const { handleOnBlurValidation, errors } = useValidation();

  useEffect(() => {
    setIsValid(isValidForm());
  });

  if (submitScreen) {
    return <SubmitSuccess />;
  }

  function isValidForm() {
    return (
      identityCardFormRef.current &&
      identityCardFormRef.current.checkValidity() &&
      !imageErrors.employeePrevSezCard &&
      !imageErrors.employeePhoto
    );
  }

  const handleOnBlurInput = event => handleOnBlurValidation(event);

  const handleOnChangeIdCardFormDetails = ({ target: { name, value } }) => {
    let updatedValue = value;
    if (startingSpacePattern.test(value)) {
      updatedValue = value.trimStart();
    }
    setEmployeeIdDetails({ ...employeeIdDetails, [name]: updatedValue });
  };

  const handleHasPreviousSezCard = ({ target: { name, value } }) => {
    if (value === "0") {
      setImageErrors({ ...imageErrors, employeePrevSezCard: false });
      setEmployeeIdDetails({
        ...employeeIdDetails,
        [name]: value,
        employeePrevSezCard: undefined
      });
    } else {
      setEmployeeIdDetails({
        ...employeeIdDetails,
        [name]: value
      });
    }
  };
  const handleImageOnChange = ({ target: { files, name } }) => {
    const image = files[0];
    if (image && image.size > 2097152)
      setImageErrors({ ...imageErrors, [name]: true });
    else setImageErrors({ ...imageErrors, [name]: false });

    setEmployeeIdDetails({ ...employeeIdDetails, [name]: image });
  };

  const saveEmployeeIdFormDetails = async () => {
    setSubmitAlert(false);
    setSubmitLoading(true);
    try {
      const {
        employeePhoto,
        employeePrevSezCard,
        hasPreviousSezCard
      } = employeeIdDetails;

      const formData = new FormData(identityCardFormRef.current);
      formData.append("status", "1");
      const employeePhotoCompressedImage = await imageCompressor(employeePhoto);
      formData.set("employeePhoto", employeePhotoCompressedImage);
      if (hasPreviousSezCard === "1" && employeePrevSezCard) {
        const employeePrevSezCardCompressedImage = await imageCompressor(
          employeePrevSezCard
        );
        formData.set("employeePrevSezCard", employeePrevSezCardCompressedImage);
      } else {
        formData.set("employeePrevSezCard", null);
      }

      const addSEZCardDetailsResponse = await api.addSEZCardDetails(formData);
      if (
        addSEZCardDetailsResponse &&
        !addSEZCardDetailsResponse.errorMessage
      ) {
        setSubmitScreen(true);
      } else {
        if (
          addSEZCardDetailsResponse.errorMessage ===
          "Employee details already exist in the system"
        )
          setErrorMessage(
            "Details with this Employee Id already exists. Facilities team will contact if required."
          );
        else setErrorMessage("Error while Uploading Details");
        setErrorAlert(true);
      }
    } catch (e) {
      toast.error(`Error while Uploading Details`, defaultToastOptions);
    }
    setSubmitLoading(false);
  };

  const currentDate = moment(new Date()).format("YYYY-MM-DD");
  return (
    <Fragment>
      <Form className="w-60 pt-3 m-auto" noValidate ref={identityCardFormRef}>
        <div className="container w-100 mx-auto org-details approval-form">
          <Card>
            <Card.Header className="accordian-header text-left">
              <Row>
                <Col sm={12} className="text-center">
                  SEZ Card Renewal Form
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Form.Group as={Row} controlId="employeeId">
                <Form.Label column sm={4} className="text-right">
                  Employee ID*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    isInvalid={errors.employeeId}
                    name="employeeId"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    pattern={positiveNumberPattern}
                    placeholder="Eg: 123456"
                    required
                    size="sm"
                    value={employeeIdDetails.employeeId}
                  />
                  {errors.employeeId && (
                    <Form.Control.Feedback type="invalid">
                      {errors.employeeId.type === "required" &&
                        "Employee ID is required"}
                      {errors.employeeId.type === "pattern" &&
                        "Employee ID should be a numeric value"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="employeeName">
                <Form.Label column sm={4} className="text-right">
                  Name Of The Employee*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    isInvalid={errors.employeeName}
                    name="employeeName"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    placeholder="Full Name"
                    required
                    size="sm"
                    type="text"
                    value={employeeIdDetails.employeeName}
                  />
                  {errors.employeeName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.employeeName.type === "required" &&
                        "Name of the Employee is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="designation">
                <Form.Label column sm={4} className="text-right">
                  Designation*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    isInvalid={errors.designation}
                    name="designation"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    placeholder="Job Title"
                    required
                    size="sm"
                    type="text"
                    value={employeeIdDetails.designation}
                  />
                  {errors.designation && (
                    <Form.Control.Feedback type="invalid">
                      {errors.designation.type === "required" &&
                        "Designation is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="relativeName">
                <Form.Label column sm={4} className="pl-1 text-right">
                  Father’s/Mother’s/Husband’s Name*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    className="mt-2"
                    isInvalid={errors.relativeName}
                    name="relativeName"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    required
                    size="sm"
                    type="text"
                    value={employeeIdDetails.relativeName}
                  />
                  {errors.relativeName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.relativeName.type === "required" &&
                        "Father’s /Mother’s/ Husband’s Name is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="relationShip">
                <Form.Label column sm={4} className="text-right">
                  Relationship*
                </Form.Label>
                <Col sm={6}>
                  <div className="mt-2">
                    <Form.Check
                      id="father-relation"
                      inline
                      label="Father"
                      name="relationShipWithMe"
                      onChange={handleOnChangeIdCardFormDetails}
                      required
                      type="radio"
                      value="1"
                    />
                    <Form.Check
                      id="mother-relation"
                      inline
                      label="Mother"
                      name="relationShipWithMe"
                      onChange={handleOnChangeIdCardFormDetails}
                      required
                      type="radio"
                      value="2"
                    />
                    <Form.Check
                      id="husband-relation"
                      inline
                      label="Husband"
                      name="relationShipWithMe"
                      onChange={handleOnChangeIdCardFormDetails}
                      required
                      type="radio"
                      value="3"
                    />
                  </div>
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="temporaryAddress">
                <Form.Label column sm={4} className="text-right">
                  Current Address*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    as="textarea"
                    isInvalid={errors.temporaryAddress}
                    maxLength={150}
                    name="temporaryAddress"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    required
                    rows={3}
                    size="sm"
                    value={employeeIdDetails.temporaryAddress}
                  />
                  {errors.temporaryAddress && (
                    <Form.Control.Feedback type="invalid">
                      {errors.temporaryAddress.type === "required" &&
                        "Current Address is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="permanentResidenceAddress">
                <Form.Label column sm={4} className="text-right">
                  Permanent Residence Address*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    as="textarea"
                    isInvalid={errors.permanentResidenceAddress}
                    maxLength={150}
                    name="permanentResidenceAddress"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    required
                    rows={3}
                    size="sm"
                    value={employeeIdDetails.permanentResidenceAddress}
                  />
                  {errors.permanentResidenceAddress && (
                    <Form.Control.Feedback type="invalid">
                      {errors.permanentResidenceAddress.type === "required" &&
                        "Permanent Residence Address is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="dateOfBirth">
                <Form.Label column sm={4} className="text-right">
                  Date of Birth*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    className="w-50"
                    isInvalid={errors.dateOfBirth}
                    max={currentDate}
                    name="dateOfBirth"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    required
                    size="sm"
                    type="date"
                    value={
                      employeeIdDetails.dateOfBirth &&
                      moment(employeeIdDetails.dateOfBirth).format("YYYY-MM-DD")
                    }
                  />
                  {errors.dateOfBirth && (
                    <Form.Control.Feedback type="invalid">
                      {errors.dateOfBirth.type === "required" &&
                        "Date of Birth is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="dateOfJoining">
                <Form.Label column sm={4} className="text-right">
                  Date of Joining*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    className="w-50"
                    isInvalid={errors.dateOfJoining}
                    max={currentDate}
                    name="dateOfJoining"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    required
                    size="sm"
                    type="date"
                    value={
                      employeeIdDetails.dateOfJoining &&
                      moment(employeeIdDetails.dateOfJoining).format(
                        "YYYY-MM-DD"
                      )
                    }
                  />
                  {errors.dateOfJoining && (
                    <Form.Control.Feedback type="invalid">
                      {errors.dateOfJoining.type === "required" &&
                        "Joining Date is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="identificationMark">
                <Form.Label column sm={4} className="text-right">
                  Identification Mark*
                </Form.Label>
                <Col sm={6}>
                  <Form.Control
                    isInvalid={errors.identificationMark}
                    name="identificationMark"
                    onBlur={handleOnBlurInput}
                    onChange={handleOnChangeIdCardFormDetails}
                    required
                    size="sm"
                    type="text"
                    value={employeeIdDetails.identificationMark}
                  />
                  {errors.identificationMark && (
                    <Form.Control.Feedback type="invalid">
                      {errors.identificationMark.type === "required" &&
                        "Identification Mark is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Form.Group>

              <Form.Group as={Row} controlId="employeePhoto">
                <Form.Label column sm={4} className="text-right">
                  Upload Profile Image*
                </Form.Label>
                <Col sm={6} className="d-flex flex-wrap">
                  <Form.File className="w-60">
                    <Form.File.Input
                      accept="image/*"
                      name="employeePhoto"
                      onChange={handleImageOnChange}
                      required
                    />
                  </Form.File>
                  {imageErrors.employeePhoto ? (
                    <p className="text-danger mt-3">
                      Image size must be less than 2 MB
                    </p>
                  ) : (
                    employeeIdDetails.employeePhoto && (
                      <div className="profile-container mt-1">
                        <Image
                          alt={
                            employeeIdDetails.employeePhoto &&
                            employeeIdDetails.employeePhoto.name
                          }
                          className="img-responsive border rounded border-dark p-1 ml-auto"
                          height="165px"
                          src={
                            employeeIdDetails.employeePhoto &&
                            URL.createObjectURL(employeeIdDetails.employeePhoto)
                          }
                          width="150px"
                        />
                      </div>
                    )
                  )}
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="currentlySEZCard">
                <Form.Label column sm={4} className="text-right">
                  Do you currently have SEZ card?*
                </Form.Label>
                <Col sm={6}>
                  <div className="mt-2">
                    <Form.Check
                      id="yes"
                      inline
                      label="Yes"
                      name="hasPreviousSezCard"
                      onChange={handleHasPreviousSezCard}
                      required
                      type="radio"
                      value="1"
                    />
                    <Form.Check
                      id="no"
                      inline
                      label="No"
                      name="hasPreviousSezCard"
                      onChange={handleHasPreviousSezCard}
                      required
                      type="radio"
                      value="0"
                    />
                  </div>
                </Col>
              </Form.Group>
              {employeeIdDetails.hasPreviousSezCard &&
                (employeeIdDetails.hasPreviousSezCard === "1" ? (
                  <Form.Group as={Row} controlId="employeePrevSezCard">
                    <Form.Label column sm={4} className="text-right">
                      <Fragment>
                        Upload Existing SEZ Card Image*
                        <p className="id-note">
                          (Note: Upload previous ID image)
                        </p>
                      </Fragment>
                    </Form.Label>
                    <Col sm={6} className="d-flex flex-wrap">
                      <Form.File className="w-60">
                        <Form.File.Input
                          accept="image/*"
                          name="employeePrevSezCard"
                          required
                          onChange={handleImageOnChange}
                        />
                      </Form.File>
                      {imageErrors.employeePrevSezCard ? (
                        <p className="text-danger mt-3">
                          Image size must be less than 2 MB
                        </p>
                      ) : (
                        employeeIdDetails.employeePrevSezCard && (
                          <div className="profile-container mt-1">
                            <Image
                              alt={
                                employeeIdDetails.employeePrevSezCard &&
                                employeeIdDetails.employeePrevSezCard.name
                              }
                              className="img-responsive border rounded border-dark p-1 ml-auto"
                              height="230px"
                              src={
                                employeeIdDetails.employeePrevSezCard &&
                                URL.createObjectURL(
                                  employeeIdDetails.employeePrevSezCard
                                )
                              }
                              width="160px"
                            />
                          </div>
                        )
                      )}
                    </Col>
                  </Form.Group>
                ) : (
                  <Form.Group as={Row} controlId="comments">
                    <Col sm={4} />
                    <Col sm={6}>
                      <Form.Control
                        as="textarea"
                        placeholder="Please enter the reason for not having SEZ Card*"
                        isInvalid={errors.comments}
                        maxLength={150}
                        name="comments"
                        onBlur={handleOnBlurInput}
                        onChange={handleOnChangeIdCardFormDetails}
                        required
                        rows={3}
                        size="sm"
                        value={employeeIdDetails.comments}
                      />
                      {errors.comments && (
                        <Form.Control.Feedback type="invalid">
                          {errors.comments.type === "required" &&
                            "Comment is required"}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  </Form.Group>
                ))}
              <Form.Group
                as={Row}
                className="justify-content-center mt-4"
                controlId="existingIdImage"
              >
                <Col className="d-flex justify-content-center">
                  <Button
                    onClick={() => setSubmitAlert(true)}
                    disabled={!isValid}
                  >
                    SUBMIT
                  </Button>
                </Col>
              </Form.Group>
            </Card.Body>
          </Card>
          <PageSpinner show={submitLoading} />
        </div>
      </Form>

      {submitAlert && (
        <Dialog
          header="Confirmation"
          message="You can submit this form only once, so please verify the details before proceeding to submit."
          closeButtonText="CANCEL"
          saveButtonText="PROCEED"
          handleClose={() => setSubmitAlert(false)}
          handleSave={saveEmployeeIdFormDetails}
        />
      )}

      {errorAlert && (
        <ErrorDialog
          header="Error"
          message={errorMessage}
          closeButtonText="Close"
          handleClose={() => setErrorAlert(false)}
        />
      )}
    </Fragment>
  );
};

export default SEZForm;
